import { Decimal } from 'decimal.js';
import { utils } from 'ethers';
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { Checkbox, Slider } from '@mui/material';
import Alert from '@mui/material/Alert';
import { observer } from 'mobx-react';
import { useAccount, useWalletClient } from 'wagmi';
import './OpenPosition.scss';
import { ModalContext } from '../../context-providers/ModalProvider';
import { ServicesContext } from '../../context-providers/ServicesProvider';
import { tokenDecimals } from '../../helpers/config/contracts.config';
import { hardcodedErrorCodes } from '../../helpers/error-message.helper';
import { formatCurrencyAmount } from '../../helpers/formatters';
import { calculatePositionSize } from '../../helpers/math/position-math.helpers';
import {
    calculateStopLossLimitWithVolatility,
    calculateTakeProfitLimitWithVolatility
} from '../../helpers/math/take-profit-stop-loss.limit-helper';
import { textToTextNumberModifier } from '../../helpers/text-to-number-modifier';
import { balanceStore } from '../../stores/BalanceStore';
import { chartStore } from '../../stores/ChartStore';
import { IMergedMarketData, IMinMaxLeverage } from '../../stores/MarketsStore';
import AmountInput from '../AmountInput/AmountInput';
import LabelValue from '../LabelValue/LabelValue';
import EditPosition, { TpSlParams, TpSlPositionParams } from '../modals/EditPosition/EditPosition';
import OpenPositionDialog from '../modals/OpenPosition/OpenPositionDialog';
import NereusButton from '../NereusButton/NereusButton';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NereusConnectButton from '../NereusConnectButton/NereusConnectButton';

interface IOpenPositionProps {
    marketInfo: IMergedMarketData;
}

const defaultMinMaxLeverage: IMinMaxLeverage = {
    minLeverage: 1,
    maxLeverage: 1
};

interface ITakeProfitStopLossPlaceholders {
    takeProfit: ReactNode;
    stopLoss: ReactNode;
}

const buttonTexts = {
    enterAmount: 'Enter Amount',
    insufficientFunds: 'Insufficient Funds',
    long: 'Long {asset}',
    short: 'Short {asset}',
    amountRange: 'Incorrect amount',
    tpRange: 'Incorrect TP',
    slRange: 'Incorrect SL'
};

export interface IMinMaxValue {
    min: number;
    max: number;
}

interface IMinMaxValuesCollection {
    amount: IMinMaxValue;
    tp: IMinMaxValue;
    sl: IMinMaxValue;
}

const morePlaceholder = <span className="OpenPosition__tp-sl-placeholder nereus-body-m"><span
    className="nereus-color-neutral-grey">≥</span><span
    className="nereus-color-white">$</span></span>;
const lessPlaceholder = <span className="OpenPosition__tp-sl-placeholder nereus-body-m"><span
    className="nereus-color-neutral-grey">≤</span><span
    className="nereus-color-white">$</span></span>;

// engine will not work with less amount
const STATIC_MIN_AMOUNT_USDT = 5;


const OpenPosition: React.FC<IOpenPositionProps> = observer(({ marketInfo }) => {
    const { address } = useAccount();
    const { openModal } = useContext(ModalContext);
    const { positionsManager } = useContext(ServicesContext);

    const [depositAmount, setDepositAmount] = useState<string>('');
    const [leverage, setLeverage] = useState<number | null>(null);
    const [positionSize, setPositionSize] = useState<number>(0);
    const [isLong, setIsLong] = useState<boolean>(!!marketInfo.long);
    const [currentMarketData, setCurrentMarketData] = useState(marketInfo.long || marketInfo.short || null)
    const [takeProfit, setTakeProfit] = useState<string | null>(null);
    const [stopLoss, setStopLoss] = useState<string | null>(null);
    const [tpSlEnabled, setTpSlEnabled] = useState<boolean>(false);
    const [minMaxLeverage, setMinMaxLeverage] = useState<IMinMaxLeverage>(marketInfo.long || marketInfo.short || defaultMinMaxLeverage);
    const [sliderMarks, setSliderMarks] = useState<any[]>([]);
    const [symbol, setSymbol] = useState<string | null>(null);
    const [infoPercentNode, setInfoPercentNode] = useState<ReactNode | null>(null);
    const [takeProfitStopLossPlaceholders, setTakeProfitStopLossPlaceholders] = useState<ITakeProfitStopLossPlaceholders>({
        takeProfit: null,
        stopLoss: null
    });

    const [mockedPositionForAdvancedModal, setMockedPositionForAdvancedModal] = useState<TpSlPositionParams>({
        symbol: '',
        amount: '0',
        leverage: '0',
        initialPrice: '0',
        lastPrice: '0'
    });

    const [error, setError] = useState<string | null>(null);
    const [ctaButtonText, setCtaButtonText] = useState<string>(buttonTexts.enterAmount);
    let [isDisabled, setIsDisabled] = useState<boolean>(false);

    const [stopLossLimit, setStopLossLimit] = useState<null | number>(null);
    const [takeProfitLimit, setTakeProfitLimit] = useState<null | number>(null);
    const [minMaxValues, setMinMaxValues] = useState<IMinMaxValuesCollection>({
        amount: {
            min: STATIC_MIN_AMOUNT_USDT,
            max: Infinity
        },
        tp: {
            min: 0,
            max: Infinity
        },
        sl: {
            min: 0,
            max: Infinity
        }
    });

    function resetForm() {
        setDepositAmount('0');
        setLeverage(minMaxLeverage.minLeverage);
        setTakeProfit(null);
        setStopLoss(null);
    }

    const getAllowCtaText = () => {
        const textTemplate = isLong ? buttonTexts.long : buttonTexts.short;
        return textTemplate.replace('{asset}', marketInfo.baseAsset?.toUpperCase() || '')
    };

    useEffect(() => {

        if (!currentMarketData) {
            setInfoPercentNode(null);
            return;
        }
        const percent = new Decimal(currentMarketData.settlementFee || 0).mul(100);
        const period = currentMarketData.settlementPeriod === '1h' ? 'hour' : currentMarketData.settlementPeriod;
        setInfoPercentNode(<span>{percent.toString()}% / {period}</span>);
    }, [currentMarketData]);

    useEffect(() => {
        const allowedMinAmount =  Math.max(Math.ceil(Number(currentMarketData?.minVolume) * chartStore.latestPrice), STATIC_MIN_AMOUNT_USDT);
        const allowedMaxAmount = Math.floor(Number(currentMarketData?.maxVolume) * chartStore.latestPrice);

        let tpMin = 0;
        let tpMax = Infinity;
        let slMin = 0;
        let slMax = Infinity;

        if (isLong) {
            tpMin = chartStore.latestPrice;
            tpMax = takeProfitLimit || Infinity;
            slMax = chartStore.latestPrice;
            slMin = stopLossLimit || 0;
        } else {
            tpMin = takeProfitLimit || 0;
            tpMax = chartStore.latestPrice;
            slMin = chartStore.latestPrice;
            slMax = stopLossLimit || Infinity;
        }

        setMinMaxValues({
            amount: {
                min: allowedMinAmount,
                max: allowedMaxAmount
            },
            tp: {
                min: tpMin,
                max: tpMax
            },
            sl: {
                min: slMin,
                max: slMax
            }
        });

    }, [stopLossLimit, takeProfitLimit, isLong, depositAmount, chartStore.latestPrice, currentMarketData]);

    useEffect(() => {
        setStopLossLimit(calculateStopLossLimitWithVolatility(!isLong, leverage || 1, chartStore.latestPrice).toNumber());
    }, [isLong, leverage, chartStore.latestPrice]);

    useEffect(() => {
        setTakeProfitLimit(calculateTakeProfitLimitWithVolatility(!isLong, leverage || 1, chartStore.latestPrice, marketInfo.baseAsset).toNumber());
    }, [isLong, leverage, chartStore.latestPrice, marketInfo.baseAsset]);

    useEffect(() => {
        setCurrentMarketData((isLong ? marketInfo.long : marketInfo.short) || null);
    }, [marketInfo, isLong]);

    useEffect(() => {
        const symbol = marketInfo?.[isLong ? 'long' : 'short']?.symbol;
        setSymbol(symbol || null);
    }, [marketInfo, isLong])

    useEffect(() => {
        setMockedPositionForAdvancedModal({
            symbol: symbol || '',
            tpPrice: takeProfit ? takeProfit.toString() : undefined,
            slPrice: stopLoss ? stopLoss.toString() : undefined,
            amount: utils.parseUnits((depositAmount || 0).toString(), tokenDecimals.usdt).toString(),
            leverage: leverage ? leverage.toString() : '0',
            initialPrice: chartStore.latestPrice?.toString() || '0',
            lastPrice: chartStore.latestPrice?.toString() || '0'
        });
    }, [symbol, takeProfit, stopLoss, depositAmount, leverage, chartStore.latestPrice]);

    useEffect(() => {
        const currentMarketMinLeverage = isLong ? marketInfo.long?.minLeverage : marketInfo.short?.minLeverage;
        const currentMarketMaxLeverage = isLong ? marketInfo.long?.maxLeverage : marketInfo.short?.maxLeverage;
        if (!leverage || (currentMarketMinLeverage && (leverage < currentMarketMinLeverage)) || (currentMarketMaxLeverage && (leverage > currentMarketMaxLeverage))) {
            setLeverage(Number(currentMarketMinLeverage));
        }
    }, [marketInfo, isLong]);

    useEffect(() => {
        if (!marketInfo.long && !marketInfo.short) {
            return;
        }
        const currentConfigurationExists = !!(isLong ? marketInfo.long : marketInfo.short);

        if (!currentConfigurationExists) {
            setIsLong(!isLong);
        }
    }, [marketInfo, isLong]);

    useEffect(() => {
        const numberDepositAmount = Number(depositAmount);
        if (numberDepositAmount <= 0) {
            setCtaButtonText(buttonTexts.enterAmount);

            return;
        }

        if (numberDepositAmount > Number(balanceStore.usdtAvailableBalanceDecimal)) {
            setCtaButtonText(buttonTexts.insufficientFunds);
            setError(hardcodedErrorCodes.FE_NOT_ENOUGH_BALANCE_TO_OPEN_POSITION);
            return;
        }

        if (numberDepositAmount < minMaxValues.amount?.min || numberDepositAmount > minMaxValues.amount?.max) {
            setCtaButtonText(buttonTexts.amountRange);
            setError(hardcodedErrorCodes.FE_OPEN_POSITION_AMOUNT_RANGE
                .replace(/{min}/, formatCurrencyAmount(minMaxValues.amount?.min, '$'))
                .replace(/{max}/, formatCurrencyAmount(minMaxValues.amount?.max, '$'))
            );
            return;
        }

        const takeProfitNum = Number(takeProfit);
        const stopLossNum = Number(stopLoss);

        if (takeProfitNum) {
            if (takeProfitNum < minMaxValues.tp.min || takeProfitNum > minMaxValues.tp.max) {
                setCtaButtonText(buttonTexts.tpRange);
                setError(hardcodedErrorCodes.FE_TP_RANGE
                    .replace(/{min}/, formatCurrencyAmount(minMaxValues.tp?.min, '$'))
                    .replace(/{max}/, formatCurrencyAmount(minMaxValues.tp?.max, '$'))
                );
                return;
            }
        }
        if (stopLossNum) {
            if (stopLossNum < minMaxValues.sl.min || stopLossNum > minMaxValues.sl.max) {
                setCtaButtonText(buttonTexts.slRange);
                setError(hardcodedErrorCodes.FE_SL_RANGE
                    .replace(/{min}/, formatCurrencyAmount(minMaxValues.sl?.min, '$'))
                    .replace(/{max}/, formatCurrencyAmount(minMaxValues.sl?.max, '$'))
                );
                return;
            }
        }
        setCtaButtonText(getAllowCtaText());
        setError(null);
    }, [minMaxValues, depositAmount, takeProfit, stopLoss, marketInfo.baseAsset, balanceStore.usdtAvailableBalanceDecimal]);

    useEffect(() => {
        setTakeProfitStopLossPlaceholders({
            takeProfit: isLong ? morePlaceholder : lessPlaceholder,
            stopLoss: isLong ? lessPlaceholder : morePlaceholder
        });
        setStopLoss(null);
        setTakeProfit(null);
    }, [isLong]);

    useEffect(() => {
        if (getAllowCtaText() === ctaButtonText && !error) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    }, [ctaButtonText, error, marketInfo.baseAsset])

    useEffect(() => {
        if (!tpSlEnabled) {
            setTakeProfit(null);
            setStopLoss(null);
        }
    }, [tpSlEnabled]);

    useEffect(() => {
        const minLeverage = minMaxLeverage.minLeverage;
        const maxLeverage = minMaxLeverage.maxLeverage;

        const interval = (maxLeverage - minLeverage) / 6;

        const marks = Array.from({ length: 7 }, (_, index) => {
            const value = minLeverage + index * interval;
            const labelClass = Number(leverage) < value ? 'nereus-color-neutral-grey' : (isLong ? 'nereus-color-main' : 'nereus-color-status-warning');
            return {
                value: Math.round(value),
                label: <span className={labelClass}>x{Math.round(value)}</span>
            };
        });
        setSliderMarks(marks);
    }, [minMaxLeverage, leverage, isLong])

    const { data: signer } = useWalletClient();

    useEffect(() => {
        if (address) {
            balanceStore.fetchBalance(address);
        }
    }, [address, balanceStore]);

    useEffect(() => {
        let newMinMaxLeverageSettings;
        if (isLong) {
            newMinMaxLeverageSettings = marketInfo.long && { minLeverage: marketInfo.long.minLeverage, maxLeverage: marketInfo.long.maxLeverage };
        } else {
            newMinMaxLeverageSettings = marketInfo.short && { minLeverage: marketInfo.short.minLeverage, maxLeverage: marketInfo.short.maxLeverage };
        }
        setMinMaxLeverage(newMinMaxLeverageSettings || defaultMinMaxLeverage);
    }, [marketInfo, isLong]);

    useEffect(() => {
        const size = calculatePositionSize(chartStore.latestPrice, leverage || 1, Number(depositAmount), isLong);
        setPositionSize(size || 0);
    }, [depositAmount, leverage, isLong, chartStore.latestPrice, positionsManager]);

    const setTpSlFromAdvanced = (tpSlParams: TpSlParams) => {
        setTakeProfit(tpSlParams.tp ? tpSlParams.tp + '' : '');
        setStopLoss(tpSlParams.sl ? tpSlParams.sl + '' : '');
    }

    const openAdvanced = () => {
        openModal(<EditPosition title={
            <h2 className="nereus-body-m">Take Profit / Stop Loss - <span className="nereus-color-main">Advanced</span>
            </h2>
        } applyChanges={setTpSlFromAdvanced} position={mockedPositionForAdvancedModal}/>);
    }

    const handleDepositChange = (value: string) => {
        setDepositAmount(value);
    };

    const handleLeverageChange = (_e: Event, value: number | number[]) => {
        setLeverage(value as number);
    };

    const handleTpSlToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTpSlEnabled(event.target.checked);
    };

    const handleTakeProfitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTakeProfit(textToTextNumberModifier(event.target.value, tokenDecimals.usdt));
    };

    const handleStopLossChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStopLoss(textToTextNumberModifier(event.target.value, tokenDecimals.usdt));
    };

    const openNewPosition = async () => {
        if (!signer || !address) {
            return;
        }
        const symbol = marketInfo?.[isLong ? 'long' : 'short']?.symbol;

        if (!symbol) {
            return;
        }

        openModal(<OpenPositionDialog
            symbol={symbol}
            amount={Number(depositAmount)}
            leverage={Number(leverage)}
            onOpenPositionStarted={() => resetForm()}
            takeProfit={Number(takeProfit)}
            stopLoss={Number(stopLoss)}
        />);
    }

    //@todo temp disable markets, remove when markets will be removed
    if (marketInfo.baseAsset.toLowerCase() === 'trumpwins') {
        isDisabled = true
    } else if (marketInfo.baseAsset.toLowerCase() === 'harriswins') {
        isDisabled = true
    }

    // opening new positions is disabled
    isDisabled = true

    return (<>
        <div className="OpenPosition card-container p-4 mt-4">
            <div className="OpenPosition__trade-type-buttons mb-3 flex">
                {marketInfo.long && <NereusButton color={isLong ? 'primary' : 'secondary'}
                                                  className={`flex-1 long-btn ${isLong ? 'active' : ''}`}
                                                  variant="outlined" onClick={() => setIsLong(true)}>
                    <ArrowOutwardIcon className="h-5 mr-1"/> Long
                </NereusButton>}
                {marketInfo.short &&
                    <NereusButton className={`flex-1 short-btn ${!isLong ? 'active' : ''}`}
                                  color={isLong ? 'secondary' : 'orange'} variant="outlined"
                                  onClick={() => setIsLong(false)}>
                        <ArrowOutwardIcon className="rotate-90 h-5 mr-1"/> Short
                    </NereusButton>}
            </div>

            <AmountInput label="Pay" asset={marketInfo.quoteAsset} hasMaxButton={true} placeholder="0.00" value={depositAmount || ''}
                         available={balanceStore.usdtAvailableBalanceDecimal} onChange={handleDepositChange}/>

            <div className="OpenPosition__delimiter">
                <KeyboardArrowDownIcon/>
            </div>

            <AmountInput label={isLong ? 'Long' : 'Short'} placeholder="0.00" asset={marketInfo.baseAsset}
                         value={positionSize ? positionSize.toFixed(4) : ''}/>

            <div className="OpenPosition__multiply-container">
                <div className="mb-3">
                    <label className="OpenPosition__multiply-container__label">Multiply</label>
                </div>
                <div className="nereus-title-m">
                    <span className="nereus-color-neutral-grey">x</span>{leverage}
                </div>
                <div className="pl-2 pr-2">
                    <Slider
                        aria-label="Temperature"
                        valueLabelDisplay="auto"
                        value={leverage || 0}
                        size="small"
                        step={1}
                        marks={sliderMarks}
                        color={isLong ? 'primary' : 'warning'}
                        min={minMaxLeverage.minLeverage}
                        max={minMaxLeverage.maxLeverage}
                        onChange={handleLeverageChange}
                    />
                </div>
            </div>
            <div className="mt-3.5 mb-3.5 block cursor-pointer flex justify-between items-center nereus-caption">
                <label><Checkbox size="small" checked={tpSlEnabled} onChange={handleTpSlToggle}/>&nbsp;Set TP /
                    SL</label>
                {tpSlEnabled && <span onClick={openAdvanced}
                                      className="OpenPosition__advanced-link nereus-callout-m nereus-color-main">Advanced</span>}
            </div>

            {tpSlEnabled && (
                <div className="OpenPosition__takeProfitStopLoss">
                    <label className="block nereus-caption nereus-color-neutral-grey">Take Profit</label>
                    <div className="OpenPosition__takeProfitStopLoss__inputWrapper mt-2">
                        {takeProfitStopLossPlaceholders.takeProfit}
                        <input className="nereus-input w-full"
                               type="text"
                               value={takeProfit || ''}
                               onChange={handleTakeProfitChange}
                               placeholder="Not set"
                        />
                    </div>
                    <label className="mt-5 block nereus-caption nereus-color-neutral-grey">Stop Loss</label>
                    <div className="OpenPosition__takeProfitStopLoss__inputWrapper mb-3.5 mt-2">
                        {takeProfitStopLossPlaceholders.stopLoss}
                        <input
                            type="text"
                            className="nereus-input w-full"
                            value={stopLoss || ''}
                            onChange={handleStopLossChange}
                            placeholder="Not set"
                        />
                    </div>
                </div>
            )}

            <NereusConnectButton>
                <NereusButton
                    className="w-full"
                    onClick={openNewPosition}
                    disabled={isDisabled}
                    variant="contained"
                >
                    {ctaButtonText}
                </NereusButton>

                {error && <Alert className="OpenPosition__error mt-4" variant="standard" severity="error">
                    <span className="nereus-caption">{error}</span>
                </Alert>}
            </NereusConnectButton>
        </div>

        <div className="OpenPoitionInfo card-container p-4 mt-4">
            <LabelValue label="Collateral In" value={marketInfo.quoteAsset?.toUpperCase()}/>
            <LabelValue label="Profit / Loss in" value={marketInfo.quoteAsset?.toUpperCase()}/>
            <LabelValue label="Est. Open Price" value={formatCurrencyAmount(chartStore.latestPrice, '$')}/>
            {infoPercentNode ? <LabelValue label="Rollover Fee" value={infoPercentNode}/> : null}
        </div>
    </>);
});

export default OpenPosition;
